import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Star, Zap, Crown, Package, Stethoscope, Users, FileText } from 'lucide-react';
import PayFastButton from './PayFastButton';
import { callGetPlanCatalog, callGrantDevEntitlement, readCachedPlanCatalog } from '../../services/functions';

interface PlanCardProps {
  name: string;
  description: string;
  features?: string[];
  limits?: string[];
  productId: 'vetaxis' | 'staffaxis' | 'noteaxis' | 'suite';
  planId: string;
  label?: string;
  popular?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  price?: string;
  period?: string;
  devGrantEnabled?: boolean;
  onDevGrant?: () => Promise<void>;
}

const PlanCard: React.FC<PlanCardProps> = ({
  name,
  description,
  features,
  limits,
  productId,
  planId,
  label,
  popular,
  icon: Icon,
  price,
  period,
  devGrantEnabled,
  onDevGrant
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className={`glass-effect glass-effect-hover rounded-2xl p-6 shadow-glass relative h-full flex flex-col ${
        popular ? 'ring-2 ring-primary-500 ring-opacity-50' : ''
      }`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-primary-500 text-white px-4 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
            <Star className="w-3 h-3" />
            Most Popular
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="p-2 rounded-lg bg-primary-500/10 dark:bg-primary-400/10">
              <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
          )}
          <div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">{name}</h3>
            <p className="text-sm text-slate-600 dark:text-gray-400">{description}</p>
          </div>
        </div>
      </div>

      {/* Pricing */}
      {price && (
        <div className="mb-6">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-slate-900 dark:text-white">{price}</span>
            {period && <span className="text-sm text-slate-600 dark:text-gray-400">/{period}</span>}
          </div>
        </div>
      )}

      {/* Features */}
      {features && features.length > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-gray-400 mb-2">Features</div>
          <div className="space-y-3 flex-1">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 dark:text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Limits */}
      {limits && limits.length > 0 && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-gray-400 mb-2">{productId === 'suite' ? 'What is included' : 'Limits'}</div>
          <div className="space-y-3">
            {limits.map((lim, index) => (
              <div key={index} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 dark:text-gray-300">{lim}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      <div className="mt-auto pt-4">
        <PayFastButton
          productId={productId}
          planId={planId}
          label={label}
          variant={popular ? 'primary' : 'secondary'}
          size="md"
        />
        {devGrantEnabled && (
          <div className="mt-2">
            <button onClick={onDevGrant} className="btn-outline w-full text-sm">Grant Dev Access</button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

type TabType = 'vetaxis' | 'staffaxis' | 'noteaxis' | 'bundles';

interface TabConfig {
  id: TabType;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

type CatalogPlan = {
  id: string;
  name: string;
  description?: string;
  price?: number;
  order?: number;
  active?: boolean;
  features?: Record<string, any>;
  limits?: Record<string, any>;
  tags?: string[];
  includes?: Record<string, number>;
};

const Plans: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('vetaxis');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [vetPlans, setVetPlans] = useState<CatalogPlan[]>([]);
  const [staffPlans, setStaffPlans] = useState<CatalogPlan[]>([]);
  const [notePlans, setNotePlans] = useState<CatalogPlan[]>([]);
  const [suitePlans, setSuitePlans] = useState<CatalogPlan[]>([]);
  const devGrantEnabled = String(process.env.REACT_APP_DEV_GRANT_ENABLED || 'false').toLowerCase() === 'true';
  const [devMessage, setDevMessage] = useState<string | null>(null);

  const applyCatalog = (data: Awaited<ReturnType<typeof callGetPlanCatalog>>) => {
    setVetPlans((data.vetaxis || []).filter(p => p.active !== false));
    setStaffPlans((data.staffaxis || []).filter(p => p.active !== false));
    setNotePlans((data.noteaxis || []).filter(p => p.active !== false));
    setSuitePlans((data.suite || []).filter(p => p.active !== false));
  };

  const loadCatalog = async () => {
    // Render cached plans instantly, then refresh from the server in the background
    const cached = readCachedPlanCatalog();
    if (cached) {
      applyCatalog(cached);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await callGetPlanCatalog();
      applyCatalog(data);
    } catch (e: any) {
      if (!cached) setError(e?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const fmtVal = (v: any): string => {
    if (v === undefined || v === null) return '';
    if (typeof v === 'string') return v === 'unlimited' ? 'Unlimited' : v;
    return String(v);
  };

  const nameByOrder = (pid: 'vetaxis'|'staffaxis'|'noteaxis', order?: number): string => {
    if (!order) return '';
    const src = pid === 'vetaxis' ? vetPlans : pid === 'staffaxis' ? staffPlans : notePlans;
    const found = src.find(x => x.order === order);
    return found?.name || `Plan ${order}`;
  };

  const buildLimits = (pid: 'vetaxis'|'staffaxis'|'noteaxis'|'suite', p: CatalogPlan): string[] => {
    const items: string[] = [];
    if (pid === 'vetaxis') {
      // NEW: Use limits object for hybrid model
      const limits = p.limits as any;
      const features = p.features as any;

      // Storage limits
      if (limits?.animals !== undefined) {
        items.push(`${fmtVal(limits.animals)} Animals`);
      } else if (features?.animals !== undefined) {
        items.push(`${fmtVal(features.animals)} Animals`);
      }

      if (limits?.clients !== undefined) {
        items.push(`${fmtVal(limits.clients)} Clients`);
      } else if (features?.clients !== undefined) {
        items.push(`${fmtVal(features.clients)} Clients`);
      }

      // Monthly activity limits (NEW)
      if (limits?.invoicesPerMonth !== undefined) {
        items.push(`${fmtVal(limits.invoicesPerMonth)} Invoices/month`);
      } else if (features?.invoices !== undefined) {
        items.push(`${fmtVal(features.invoices)} Invoices`);
      }

      if (limits?.clinicalJobsPerMonth !== undefined) {
        items.push(`${fmtVal(limits.clinicalJobsPerMonth)} Clinical Jobs/month`);
      } else if (features?.clinicalWork !== undefined) {
        items.push(`${fmtVal(features.clinicalWork)} Clinical Records`);
      }

      // User limits (NEW)
      if (limits?.usersIncluded !== undefined) {
        const included = limits.usersIncluded;
        const additionalCost = limits.additionalUserCost;
        const hasAdditionalCost = typeof additionalCost === 'number' && additionalCost > 0;
        if (included === 1 && hasAdditionalCost) {
          items.push(`${included} User (+R${additionalCost} per additional user)`);
        } else if (typeof included === 'number' && included > 1 && hasAdditionalCost) {
          items.push(`${included} Users (+R${additionalCost} per additional user)`);
        } else if (included === 1) {
          items.push(`${included} User`);
        } else {
          items.push(`${fmtVal(included)} Users`);
        }
      }
    }
    if (pid === 'noteaxis' && p.features) {
      const f = p.features as any;
      if (f.transcripts !== undefined) items.push(`${fmtVal(f.transcripts)} Transcripts`);
      if (f.templates !== undefined) items.push(`${fmtVal(f.templates)} Templates`);
      if (f.library_categories !== undefined) items.push(`${fmtVal(f.library_categories)} Library categories`);
    }
    if (pid === 'staffaxis' && p.limits) {
      const lim = p.limits as any;
      if (lim.users_included !== undefined) {
        items.push(lim.users_included === 'unlimited' ? 'Unlimited users' : `Includes up to ${fmtVal(lim.users_included)} users`);
      }
    }
    if (pid === 'suite' && p.includes) {
      const inc = p.includes as any;
      if (inc.vetaxis) items.push(`VetAxis ${nameByOrder('vetaxis', inc.vetaxis)}`);
      if (inc.staffaxis) items.push(`StaffAxis ${nameByOrder('staffaxis', inc.staffaxis)}`);
      if (inc.noteaxis) items.push(`NoteAxis ${nameByOrder('noteaxis', inc.noteaxis)}`);
    }
    return items;
  };

  const buildFeatures = (pid: 'vetaxis'|'staffaxis'|'noteaxis'|'suite', p: CatalogPlan): string[] => {
    const items: string[] = [];
    if (pid === 'vetaxis') {
      // Use features from the plan data if available
      if (p.features && Array.isArray(p.features)) {
        return p.features as string[];
      }

      // Fallback: Base features for all plans
      items.push('Patient Records', 'Appointment Scheduling', 'Email Support', 'Invoices, Clinical Records, Medical History');

      // Professional and higher features
      if (p.order && p.order >= 2) {
        items.push('Advanced Reporting', 'Custom Templates', 'Priority Support');
      }

      // Pro features
      if (p.order && p.order >= 3) {
        items.push('Accounting Module', 'Admissions & Hospitalization');
      }
    }
    if (pid === 'noteaxis') {
      items.push('Template library', 'Voice-to-text', 'Search & organization');
    }
    if (pid === 'staffaxis') {
      items.push('Staff Scheduling', 'Time Tracking', 'Reporting & Analytics', 'Priority Support');
    }
    if (pid === 'suite') {
      items.push('All-in-one bundle', 'Single Sign-On across apps');
    }
    return items;
  };

  // const seedAll = async () => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     await callEnsureSuitePlans();
  //     await loadCatalog();
  //   } catch (e: any) {
  //     setError(e?.message || 'Failed to seed plans');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  React.useEffect(() => { loadCatalog(); }, []);

  const grantDev = async (productId: 'vetaxis'|'staffaxis'|'noteaxis', planId: string) => {
    setDevMessage(null);
    try {
      await callGrantDevEntitlement({ productId, planId, status: 'active' });
      setDevMessage(`Granted dev access for ${productId} (${planId})`);
    } catch (e:any) {
      setDevMessage(e?.message || 'Failed to grant dev access');
    }
  };

  const getPlanIdByOrder = (pid: 'vetaxis'|'staffaxis'|'noteaxis', order?: number): string | undefined => {
    if (!order) return undefined;
    const src = pid === 'vetaxis' ? vetPlans : pid === 'staffaxis' ? staffPlans : notePlans;
    return src.find(p => p.order === order)?.id;
  };

  const grantDevBundle = async (bundle: CatalogPlan) => {
    try {
      const inc = bundle.includes || {} as any;
      if (inc.vetaxis) {
        const id = getPlanIdByOrder('vetaxis', inc.vetaxis);
        if (id) await callGrantDevEntitlement({ productId: 'vetaxis', planId: id, status: 'active' });
      }
      if (inc.staffaxis) {
        const id = getPlanIdByOrder('staffaxis', inc.staffaxis);
        if (id) await callGrantDevEntitlement({ productId: 'staffaxis', planId: id, status: 'active' });
      }
      if (inc.noteaxis) {
        const id = getPlanIdByOrder('noteaxis', inc.noteaxis);
        if (id) await callGrantDevEntitlement({ productId: 'noteaxis', planId: id, status: 'active' });
      }
      setDevMessage('Granted dev access for bundle');
    } catch (e:any) {
      setDevMessage(e?.message || 'Failed to grant bundle dev access');
    }
  };

  const tabs: TabConfig[] = [
    {
      id: 'vetaxis',
      name: 'VetAxis',
      icon: Stethoscope,
      description: 'Veterinary practice management'
    },
    {
      id: 'staffaxis',
      name: 'StaffAxis',
      icon: Users,
      description: 'Healthcare staff management'
    },
    {
      id: 'noteaxis',
      name: 'NoteAxis',
      icon: FileText,
      description: 'Clinical documentation'
    },
    {
      id: 'bundles',
      name: 'Bundles',
      icon: Package,
      description: 'Save with combined plans'
    }
  ];

  const tabVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'vetaxis':
        if (vetPlans.length) {
          return (
            <motion.div
              key="vetaxis"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {vetPlans.map((p) => (
                <PlanCard
                  key={p.id}
                  name={p.name}
                  description={p.description || ''}
                  price={p.price !== undefined ? `R${p.price}` : undefined}
                  period={p.price !== undefined ? 'month' : undefined}
                  productId="vetaxis"
                  planId={p.id}
                  popular={p.tags?.includes('popular') || p.order === 2}
                  icon={p.order === 3 ? Crown : p.order === 2 ? Check : Zap}
                  features={buildFeatures('vetaxis', p)}
                  limits={buildLimits('vetaxis', p)}
                  devGrantEnabled={devGrantEnabled}
                  onDevGrant={() => grantDev('vetaxis', p.id)}
                />
              ))}
            </motion.div>
          );
        }
        return (<div className="text-center text-slate-600 dark:text-gray-300">No VetAxis plans found.</div>);

      case 'staffaxis':
        if (staffPlans.length) {
          return (
            <motion.div
              key="staffaxis"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {staffPlans.map((p) => (
                <PlanCard
                  key={p.id}
                  name={p.name}
                  description={p.description || ''}
                  price={p.price !== undefined ? `R${p.price}` : undefined}
                  period={p.price !== undefined ? 'month' : undefined}
                  productId="staffaxis"
                  planId={p.id}
                  popular={p.tags?.includes('popular') || p.order === 2}
                  icon={p.order === 3 ? Crown : p.order === 2 ? Check : Zap}
                  features={buildFeatures('staffaxis', p)}
                  limits={buildLimits('staffaxis', p)}
                  devGrantEnabled={devGrantEnabled}
                  onDevGrant={() => grantDev('staffaxis', p.id)}
                />
              ))}
            </motion.div>
          );
        }
        return (<div className="text-center text-slate-600 dark:text-gray-300">No StaffAxis plans found.</div>);

      case 'noteaxis':
        if (notePlans.length) {
          return (
            <motion.div
              key="noteaxis"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {notePlans.map((p) => (
                <PlanCard
                  key={p.id}
                  name={p.name}
                  description={p.description || ''}
                  price={p.price !== undefined ? `R${p.price}` : undefined}
                  period={p.price !== undefined ? 'month' : undefined}
                  productId="noteaxis"
                  planId={p.id}
                  popular={p.tags?.includes('popular') || p.order === 2}
                  icon={p.order === 3 ? Crown : p.order === 2 ? Check : Zap}
                  features={buildFeatures('noteaxis', p)}
                  limits={buildLimits('noteaxis', p)}
                  devGrantEnabled={devGrantEnabled}
                  onDevGrant={() => grantDev('noteaxis', p.id)}
                />
              ))}
            </motion.div>
          );
        }
        return (<div className="text-center text-slate-600 dark:text-gray-300">No NoteAxis plans found.</div>);

      case 'bundles':
        if (suitePlans.length) {
          return (
            <motion.div
              key="bundles"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {suitePlans.map((p) => (
                <PlanCard
                  key={p.id}
                  name={p.name}
                  description={p.description || ''}
                  price={p.price !== undefined ? `R${p.price}` : undefined}
                  period={p.price !== undefined ? 'month' : undefined}
                  productId="suite"
                  planId={p.id}
                  label="Subscribe to Bundle"
                  popular={p.tags?.includes('popular') || p.id === 'suite_pro'}
                  icon={p.id === 'suite_pro' ? Crown : Package}
                  features={buildFeatures('suite', p)}
                  limits={buildLimits('suite', p)}
                  devGrantEnabled={devGrantEnabled}
                  onDevGrant={() => grantDevBundle(p)}
                />
              ))}
            </motion.div>
          );
        }
        return (<div className="text-center text-slate-600 dark:text-gray-300">No bundle plans found.</div>);

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <motion.div
          className="glass-effect rounded-2xl p-8 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <div className="text-slate-700 dark:text-gray-200">Loading plans…</div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Choose your <span className="gradient-text">perfect plan</span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-gray-300 max-w-2xl mx-auto">
            Scale your healthcare operations with our flexible pricing options
          </p>
        </motion.div>

        {/* Seed + status */}
        {/* <div className="max-w-4xl mx-auto mb-8 flex items-center justify-between">
          <button onClick={seedAll} className="btn-outline text-sm">{loading ? 'Seeding…' : 'Seed Suite + Product Plans'}</button>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div> */}

        {/* Tab Navigation */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="glass-effect rounded-2xl p-2 max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-4 rounded-xl transition-all duration-200 text-left ${
                      isActive
                        ? 'bg-primary-500 text-white shadow-lg'
                        : 'text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center gap-2">
                      <Icon className="w-6 h-6" />
                      <div>
                        <div className="font-semibold text-sm">{tab.name}</div>
                        <div className={`text-xs ${isActive ? 'text-primary-100' : 'text-slate-500 dark:text-gray-500'}`}>
                          {tab.description}
                        </div>
                      </div>
                    </div>

                    {tab.id === 'bundles' && (
                      <div className={`absolute -top-2 -right-2 px-2 py-1 rounded-full text-xs font-semibold ${
                        isActive
                          ? 'bg-white text-primary-500'
                          : 'bg-accent-500 text-white'
                      }`}>
                        Save 30%
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="min-h-[600px]"
        >
          <AnimatePresence mode="wait">
            {renderTabContent()}
          </AnimatePresence>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              Need a custom solution?
            </h3>
            <p className="text-slate-600 dark:text-gray-300 mb-6">
              Contact our team to discuss enterprise pricing and custom integrations for your organization.
            </p>
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary inline-flex items-center gap-2"
            >
              Contact Sales
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Plans;
